import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Airplane, Plus, PencilSimple, Trash, FilePdf, Receipt, MagnifyingGlass, Check, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { Card, Loading, EmptyState } from "@/components/riq";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function Trips() {
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [trips, setTrips] = useState(null);
  const [editing, setEditing] = useState(null); // trip object or {} for new
  const [managing, setManaging] = useState(null); // trip whose receipts we're managing

  const load = () => api.get("/trips").then((r) => setTrips(r.data.trips)).catch(() => setTrips(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this trip? Receipts will be unassigned (not deleted).")) return;
    try { await api.delete(`/trips/${id}`); toast.success("Trip deleted"); load(); }
    catch { toast.error("Could not delete trip"); }
  };

  const downloadTrip = async (t) => {
    try {
      const res = await api.get(`/reports/trip.pdf?trip_id=${t.id}&images=false`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      link.download = `sliply_trip_${t.name}.pdf`;
      link.click(); URL.revokeObjectURL(link.href);
    } catch { toast.error("Could not export trip"); }
  };

  if (trips === false) return <EmptyState icon={Airplane} title="Trips unavailable" hint="Please try again later." />;
  if (!trips) return <Loading />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="trips-page">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight text-riq-primary flex items-center gap-2"><Airplane size={26} weight="duotone" className="shrink-0" /> Trips</h1>
          <p className="text-muted-foreground mt-1">Group receipts into trips or holidays and export a consolidated spending report.</p>
        </div>
        <button data-testid="trips-add-btn" onClick={() => setEditing({})} className="shrink-0 flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-semibold"><Plus size={16} weight="bold" /> New trip</button>
      </div>

      {trips.length === 0 ? (
        <EmptyState icon={Airplane} title="No trips yet" hint="Create a trip, then assign receipts to it from each receipt's page." />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {trips.map((t) => (
            <Card key={t.id} data-testid={`trip-card-${t.id}`} className="flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-head font-semibold text-riq-primary truncate">{t.name}</div>
                  {t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
                  {(t.start_date || t.end_date) && <div className="text-xs text-muted-foreground mt-0.5">{t.start_date || "?"} → {t.end_date || "?"}</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`trip-edit-${t.id}`} onClick={() => setEditing(t)} className="p-1.5 rounded-lg hover:bg-secondary text-neutral-500"><PencilSimple size={16} /></button>
                  <button data-testid={`trip-delete-${t.id}`} onClick={() => remove(t.id)} className="p-1.5 rounded-lg hover:bg-secondary text-[#b3261e]"><Trash size={16} /></button>
                </div>
              </div>
              <div className="flex items-end justify-between mt-1">
                <div><div className="font-head text-xl font-bold text-riq-primary">{money(t.total, cur)}</div><div className="text-xs text-muted-foreground">{t.receipt_count} receipt{t.receipt_count === 1 ? "" : "s"}</div></div>
                <div className="flex gap-1.5">
                  <button data-testid={`trip-manage-${t.id}`} onClick={() => setManaging(t)} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary"><Receipt size={14} /> Receipts</button>
                  <button data-testid={`trip-export-${t.id}`} onClick={() => downloadTrip(t)} className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary"><FilePdf size={14} /> Export</button>
                </div>
              </div>
              {t.budget > 0 && <BudgetBar spent={t.total || 0} budget={t.budget} cur={cur} testid={`trip-budget-${t.id}`} />}
            </Card>
          ))}
        </div>
      )}

      <TripDialog editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <ManageReceiptsDialog trip={managing} trips={trips || []} cur={cur} onClose={() => setManaging(null)} onChanged={load} />
    </div>
  );
}

function BudgetBar({ spent, budget, cur, testid }) {
  const pct = Math.min((spent / budget) * 100, 100);
  const over = spent > budget;
  const near = !over && pct >= 80;
  const color = over ? "#b3261e" : near ? "var(--riq-gold, #F5A623)" : "var(--riq-primary, #6E3FFC)";
  const remaining = budget - spent;
  return (
    <div className="mt-2 pt-2 border-t border-riq-border" data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-medium text-muted-foreground">Budget {money(budget, cur)}</span>
        <span className={`font-semibold ${over ? "text-[#b3261e]" : near ? "text-riq-gold" : "text-riq-primary"}`}>
          {over ? `${money(spent - budget, cur)} over` : `${money(remaining, cur)} left`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function TripDialog({ editing, onClose, onSaved }) {
  const { user } = useAuth();
  const cur = user?.currency || "ZAR";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const open = editing !== null;
  const isEdit = editing && editing.id;

  useEffect(() => {
    if (open) {
      setName(editing.name || ""); setDescription(editing.description || "");
      setStart(editing.start_date || ""); setEnd(editing.end_date || "");
      setBudget(editing.budget != null ? String(editing.budget) : "");
    }
  }, [open]); // eslint-disable-line

  const save = async () => {
    if (!name.trim()) { toast.error("Give the trip a name"); return; }
    setBusy(true);
    const b = parseFloat(budget);
    const payload = { name, description, start_date: start || null, end_date: end || null, budget: budget !== "" && !isNaN(b) ? b : null };
    try {
      if (isEdit) await api.patch(`/trips/${editing.id}`, payload);
      else await api.post("/trips", payload);
      toast.success(isEdit ? "Trip updated" : "Trip created");
      onSaved();
    } catch { toast.error("Could not save trip"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="trip-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit trip" : "New trip"}</DialogTitle>
          <DialogDescription>Name your trip (e.g. "Cape Town client visit") and optionally set dates.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name</span>
            <input data-testid="trip-name" value={name} onChange={(e) => setName(e.target.value)} className="riq-input w-full mt-1" placeholder="Cape Town client visit" /></label>
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Description</span>
            <textarea data-testid="trip-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="riq-input w-full mt-1 resize-y" placeholder="Purpose of the trip" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Start date</span>
              <input data-testid="trip-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="riq-input w-full mt-1" /></label>
            <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">End date</span>
              <input data-testid="trip-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="riq-input w-full mt-1" /></label>
          </div>
          <label className="block"><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Spend cap / budget ({cur}) — optional</span>
            <input data-testid="trip-budget" type="number" min="0" step="0.01" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} className="riq-input w-full mt-1" placeholder="e.g. 5000" /></label>
          <button data-testid="trip-save-btn" onClick={save} disabled={busy} className="w-full rounded-full bg-riq-primary text-white py-3 font-medium disabled:opacity-60">{busy ? "Saving…" : isEdit ? "Save changes" : "Create trip"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageReceiptsDialog({ trip, trips, cur, onClose, onChanged }) {
  const open = trip !== null;
  const [recs, setRecs] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(null); // receipt id currently toggling
  const tripNames = Object.fromEntries((trips || []).map((t) => [t.id, t.name]));

  useEffect(() => {
    if (!open) { setRecs(null); setQ(""); return; }
    api.get("/receipts?limit=200")
      .then((r) => setRecs(r.data.receipts || []))
      .catch(() => setRecs(false));
  }, [open]); // eslint-disable-line

  const toggle = async (rec) => {
    const inThis = rec.trip_id === trip.id;
    setSaving(rec.id);
    try {
      await api.patch(`/receipts/${rec.id}/meta`, { trip_id: inThis ? "" : trip.id });
      setRecs((list) => list.map((x) => x.id === rec.id ? { ...x, trip_id: inThis ? null : trip.id } : x));
      onChanged();
    } catch { toast.error("Could not update receipt"); }
    finally { setSaving(null); }
  };

  const filtered = (recs || []).filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.merchant_name || "").toLowerCase().includes(s) || (r.date || "").includes(s);
  });
  const assignedCount = (recs || []).filter((r) => r.trip_id === trip?.id).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="trip-manage-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt size={20} weight="duotone" /> Receipts in "{trip?.name}"</DialogTitle>
          <DialogDescription>Tap a receipt to add it to this trip or remove it. {assignedCount} assigned.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="trip-manage-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by merchant or date"
            className="riq-input w-full pl-9" />
        </div>

        <div className="max-h-[52vh] overflow-y-auto -mx-1 px-1 space-y-1.5">
          {recs === null ? (
            <div className="py-8 flex justify-center"><CircleNotch className="animate-spin text-riq-primary" size={24} /></div>
          ) : recs === false ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Couldn't load receipts.</p>
          ) : filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No receipts found.</p>
          ) : filtered.map((r) => {
            const inThis = r.trip_id === trip.id;
            const otherTrip = r.trip_id && r.trip_id !== trip.id ? tripNames[r.trip_id] : null;
            return (
              <button key={r.id} data-testid={`trip-manage-row-${r.id}`} onClick={() => toggle(r)} disabled={saving === r.id}
                className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-60 ${inThis ? "border-riq-primary bg-riq-primary/5" : "border-riq-border hover:border-riq-primary/40"}`}>
                <span className={`shrink-0 grid place-items-center h-8 w-8 rounded-full ${inThis ? "bg-riq-primary text-white" : "bg-secondary text-neutral-400"}`}>
                  {saving === r.id ? <CircleNotch className="animate-spin" size={16} /> : inThis ? <Check size={16} weight="bold" /> : <Plus size={16} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{r.merchant_name || "Unknown merchant"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.date || "No date"}
                    {otherTrip && <span className="ml-1.5 text-riq-primary">· in "{otherTrip}"</span>}
                  </div>
                </div>
                <div className="shrink-0 font-head font-semibold text-sm text-riq-primary">{money(r.total, cur)}</div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
